import { useCallback, useEffect, useRef, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { MONACO_LAYOUT_FLUSH_EVENT } from '../components/ui/ResizableSplitter';
import { useTerminalIsDark } from './useTerminalIsDark';
import { usePreferencesStore } from '../store/preferencesStore';
import { buildTerminalTheme, clampTerminalFontSize } from '../utils/terminalTheme';
import { createShellOutputFilter } from '../utils/terminalShellFilter';
import { createTerminalXterm } from '../utils/createTerminalXterm';
import { withMacEditKeys } from '../utils/terminalMacKeys';
import { isMacPlatform } from '../utils/keyboard';
import { useTerminalOptionAsMeta } from './useTerminalOptionAsMeta';
import { acquireTerminalBus, subscribeTerminal } from './terminalOutputBus';
import { parseOsc7 } from '../utils/osc7';
import { TERMINAL_LAYOUT_SETTLED_EVENT } from '../components/terminal/terminalLayoutSettle';

const IS_WINDOWS = typeof navigator !== 'undefined' && /Win/i.test(navigator.platform || navigator.userAgent || '');


/** PTY resizes are sent on a trailing debounce: full-screen TUIs (Claude
 * Code, Codex) repaint on every resize, so a per-frame resize during a
 * divider drag makes them flicker and burns CPU. xterm itself refits per
 * frame — only the PTY waits. */
export const PTY_RESIZE_DEBOUNCE_MS = 100;

const restartedLine = () => {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `\r\n\x1b[2m── restarted ${hh}:${mm} ──\x1b[0m\r\n`;
};

/**
 * One local PTY pane: an xterm (created once, on first show) plus a session
 * in it. The xterm outlives its sessions — `restartNonce` changing starts a
 * new session in the SAME xterm, so the scrollback of the one that exited
 * stays visible above it.
 *
 * @param {object} o
 * @param {boolean} o.visible   - the pane's tab is shown
 * @param {boolean} o.focused   - the pane is its tab's focused pane (only
 *   that pane takes DOM focus when the tab is shown)
 * @param {string} [o.cwd]      - working directory for the session
 * @param {string} [o.shellKey] - shell to start (terminal_create `shell`)
 * @param {object} [o.launch]   - program launch spec instead of a shell
 * @param {number} [o.restartNonce]
 * @param {(status, code?, error?) => void} [o.onStatus] - 'starting' |
 *   'running' | 'exited' (with the exit code, null if unknown) | 'error'
 * @param {(title: string) => void} [o.onTitle] - OSC 0/2 window title
 * @param {(state: 'attached'|'server_off'|null) => void} [o.onMcp] - whether
 *   the Apiboo MCP server was attached to a launched agent (Rust decides)
 * @param {(cwd: string) => void} [o.onCwd] - OSC 7 working directory (our
 *   shell prompts send it; agents do not)
 * @param {(data: string) => void} [o.onInput] - every keystroke the user
 *   typed here (after it was sent to this pane's PTY) — broadcast fan-out
 * @param {(e: KeyboardEvent) => boolean} [o.keyFilter] - return false to keep
 *   a key away from the program (pane shortcuts)
 * @returns {{ termRef, focus: () => void, write: (data: string) => void }}
 */
export function useEmbeddedTerminal({
  visible,
  focused = true,
  cwd,
  shellKey,
  launch,
  restartNonce = 0,
  onStatus,
  onTitle,
  onCwd,
  onMcp,
  onInput,
  keyFilter,
}) {
  // The terminal's own light/dark (Settings → Extensions → Terminal → Theme).
  const isDark = useTerminalIsDark();
  const isDarkRef = useRef(isDark);
  isDarkRef.current = isDark;
  const terminalFontSize = usePreferencesStore((s) => s.terminalFontSize);
  const fontSizeRef = useRef(terminalFontSize);
  fontSizeRef.current = terminalFontSize;
  const termRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const addonsRef = useRef({});
  const commandDoneRef = useRef(null);
  const clearDividersRef = useRef(() => {});
  const sessionIdRef = useRef(null);
  const releaseBusRef = useRef(null);
  const initStartedRef = useRef(false);
  // Bumped on unmount: an xterm setup still in flight from a previous mount
  // (StrictMode, fast remounts) sees the change and abandons its result.
  const genRef = useRef(0);
  const refitRef = useRef(() => {});
  const lastPtySizeRef = useRef(null);
  const flushPtyResizeRef = useRef(() => {});
  const [ready, setReady] = useState(false);
  const optionAsMetaRef = useTerminalOptionAsMeta(xtermRef, ready);
  const cb = useRef({});
  cb.current = { onStatus, onTitle, onCwd, onMcp, onInput, keyFilter, focused, visible };

  // 1. Create the xterm once, the first time the pane is shown.
  useEffect(() => {
    if (!visible || initStartedRef.current) return;
    initStartedRef.current = true;
    const gen = genRef.current;
    (async () => {
      const release = await acquireTerminalBus();
      if (genRef.current !== gen) {
        release();
        return;
      }
      releaseBusRef.current = release;
      const x = await createTerminalXterm(termRef.current, fontSizeRef.current, isDarkRef.current, optionAsMetaRef.current);
      if (genRef.current !== gen || !x) {
        x?.term.dispose();
        return;
      }
      const { term } = x;
      xtermRef.current = term;
      fitAddonRef.current = x.fitAddon;
      addonsRef.current = x.addons || {};
      addonsRef.current.prompts?.onCommandDone((e) => commandDoneRef.current?.(e));
      clearDividersRef.current = x.clearDividers;
      term.onTitleChange?.((title) => cb.current.onTitle?.(title));
      term.parser.registerOscHandler(7, (data) => {
        const cwd = parseOsc7(data, IS_WINDOWS ? 'win' : 'unix');
        if (cwd) cb.current.onCwd?.(cwd);
        return true;
      });
      // macOS editing keys (⌥←/→, ⌘←/→, ⌥⌫, ⌘⌫) go in as the bytes shells bind.
      term.attachCustomKeyEventHandler?.(
        withMacEditKeys((e) => (cb.current.keyFilter ? cb.current.keyFilter(e) : true), (seq) => term.input(seq), isMacPlatform())
      );
      term.onData((data) => {
        const sid = sessionIdRef.current;
        if (!sid) return;
        invoke('terminal_write', { id: sid, data }).catch((err) => {
          console.error('[terminal]', err);
        });
        cb.current.onInput?.(data);
      });
      let resizeTimer = null;
      // Sends the grid to the PTY — only when it differs from what the
      // program already has, so a settle pass never repaints a TUI for nothing.
      const sendPtySize = () => {
        const sid = sessionIdRef.current;
        if (!sid || !xtermRef.current) return;
        const last = lastPtySizeRef.current;
        if (last && last.sid === sid && last.cols === term.cols && last.rows === term.rows) return;
        lastPtySizeRef.current = { sid, cols: term.cols, rows: term.rows };
        invoke('terminal_resize', { id: sid, cols: term.cols, rows: term.rows }).catch(() => {});
      };
      flushPtyResizeRef.current = () => {
        clearTimeout(resizeTimer);
        sendPtySize();
      };
      term.onResize(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(sendPtySize, PTY_RESIZE_DEBOUNCE_MS);
      });
      setReady(true);
    })().catch((err) => {
      console.error('[useEmbeddedTerminal] setup failed:', err);
      initStartedRef.current = false;
    });
  }, [visible, optionAsMetaRef]);

  // 2. A session inside the xterm; a new one per restartNonce.
  useEffect(() => {
    if (!ready) return undefined;
    const term = xtermRef.current;
    let cancelled = false;
    let sid = null;
    let unsubscribe = null;
    const filter = createShellOutputFilter(launch ? null : shellKey, {
      sendCls: () => {
        const id = sessionIdRef.current;
        if (id) invoke('terminal_write', { id, data: 'cls\r' }).catch(() => {});
      },
    });
    if (restartNonce > 0) term.write(restartedLine());
    cb.current.onStatus?.('starting');
    (async () => {
      try {
        const res = await invoke('terminal_create', {
          cwd: cwd || undefined,
          cols: term.cols,
          rows: term.rows,
          shell: shellKey || undefined,
          ...(launch ? { launch } : {}),
        });
        if (cancelled) {
          invoke('terminal_kill', { id: res.id }).catch(() => {});
          return;
        }
        sid = res.id;
        sessionIdRef.current = sid;
        cb.current.onStatus?.('running');
        cb.current.onMcp?.(res.mcp || null);
        unsubscribe = subscribeTerminal(sid, {
          onData: (chunk) => {
            const { data, cleared } = filter.push(chunk);
            if (data === null) return;
            // A screen clear wipes the text: drop the divider decorations
            // with it and let the next prompt start a fresh "first block".
            if (cleared) {
              clearDividersRef.current();
              addonsRef.current.prompts?.clear();
            }
            term.write(data);
          },
          onExit: (code) => {
            term.writeln('\r\n\x1b[33m[Process exited]\x1b[0m');
            if (sessionIdRef.current === sid) sessionIdRef.current = null;
            cb.current.onStatus?.('exited', code);
          },
        });
        if (cb.current.focused && cb.current.visible) term.focus();
      } catch (err) {
        if (cancelled) return;
        const msg = String(err?.message || err);
        term.writeln(`\r\n\x1b[31m[${msg}]\x1b[0m`);
        cb.current.onStatus?.('error', null, msg);
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe?.();
      filter.dispose();
      if (sid) invoke('terminal_kill', { id: sid }).catch(() => {});
      if (sessionIdRef.current === sid) sessionIdRef.current = null;
    };
    // A session is started per restart, with the props of that moment:
    // `cwd`/`shellKey`/`launch` changing on a live pane must not respawn it
    // (restartPane / openShellInPane bump restartNonce when they mean to).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, restartNonce]);

  useEffect(() => {
    const term = xtermRef.current;
    if (!term) return;
    term.options.theme = buildTerminalTheme(isDark);
  }, [isDark, ready]);

  // Live font-size preference (Settings → Extensions → Terminal). The refit
  // fires onResize, which tells the PTY.
  useEffect(() => {
    const term = xtermRef.current;
    if (!term) return;
    const size = clampTerminalFontSize(terminalFontSize);
    if (term.options.fontSize === size) return;
    term.options.fontSize = size;
    try {
      fitAddonRef.current?.fit();
    } catch {
      // ignore
    }
  }, [terminalFontSize, ready]);

  // Refit while shown (window resize, layout flush).
  useEffect(() => {
    if (!visible || !ready) return undefined;
    const refit = () => {
      try {
        // Hidden (display:none / 0×0): fit() would derive a garbage grid.
        if (!termRef.current || termRef.current.clientWidth === 0) return;
        fitAddonRef.current?.fit();
      } catch {
        // ignore
      }
    };
    refitRef.current = refit;
    // Layout settled (split, close, zoom, preset, drag end): refit, then give
    // the program its final size now rather than after the drag debounce.
    const settle = () => {
      refit();
      flushPtyResizeRef.current();
    };
    const timer = setTimeout(refit, 80);
    window.addEventListener('resize', refit);
    window.addEventListener(MONACO_LAYOUT_FLUSH_EVENT, refit);
    window.addEventListener(TERMINAL_LAYOUT_SETTLED_EVENT, settle);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', refit);
      window.removeEventListener(MONACO_LAYOUT_FLUSH_EVENT, refit);
      window.removeEventListener(TERMINAL_LAYOUT_SETTLED_EVENT, settle);
    };
  }, [visible, ready]);

  // Only the tab's focused pane takes DOM focus — with several panes, every
  // one grabbing focus on show would leave it on whichever mounted last.
  useEffect(() => {
    if (visible && focused && ready) xtermRef.current?.focus();
  }, [visible, focused, ready]);

  // Observe the CONTAINER, not the window: splitters, dividers and the
  // devtools footer change it without a window resize. The refit no-ops
  // while hidden or same-size.
  useEffect(() => {
    const el = termRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => refitRef.current());
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // Unmount: the session effect's cleanup kills the session; drop the xterm.
  useEffect(() => {
    return () => {
      genRef.current += 1;
      setReady(false);
      xtermRef.current?.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
      releaseBusRef.current?.();
      releaseBusRef.current = null;
      initStartedRef.current = false;
    };
  }, []);

  const focus = useCallback(() => xtermRef.current?.focus(), []);
  const write = useCallback((data) => {
    const sid = sessionIdRef.current;
    if (sid) invoke('terminal_write', { id: sid, data }).catch(() => {});
  }, []);

  // The pane's search bar and copy tools reach the xterm through these.
  const getTerm = useCallback(() => xtermRef.current, []);
  const getSearch = useCallback(() => addonsRef.current.search || null, []);
  const getPrompts = useCallback(() => addonsRef.current.prompts?.lines() || [], []);
  const getLink = useCallback(() => addonsRef.current.links?.hovered() || null, []);
  // Finished commands (card 5) go to whoever set this — the pane's notifier.
  const setOnCommandDone = useCallback((fn) => {
    commandDoneRef.current = fn;
  }, []);
  return { termRef, focus, write, getTerm, getSearch, getPrompts, getLink, setOnCommandDone };
}
